
import React, { useState } from 'react';
import { complianceService } from '../../../services/api';

interface NLPIssue {
    id: string;
    type: string;
    severity: 'RED' | 'AMBER' | 'GREEN';
    title: string;
    description: string;
    recommendation?: string;
    beforeText?: string;
    afterText?: string;
}

interface Amendment {
    id: string;
    section: string;
    severity: string;
    issue: string;
    originalText: string;
    amendedText: string;
    reason: string;
    legislationRef?: string;
}

interface AnalysisResult {
    score: number;
    status: 'PASS' | 'WARN' | 'FAIL';
    issues: NLPIssue[];
    amendments?: Amendment[];
    overallCompliance?: string;
}

interface NLPAnalysisPanelProps {
    documentText?: string;
    s3Key?: string;
    onAnalysisComplete?: (result: AnalysisResult) => void;
}

export const NLPAnalysisPanel: React.FC<NLPAnalysisPanelProps> = ({
    documentText,
    s3Key,
    onAnalysisComplete
}) => {
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<AnalysisResult | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [textInput, setTextInput] = useState(documentText || '');
    const [selectedAmendments, setSelectedAmendments] = useState<string[]>([]);

    const runAnalysis = async () => {
        setLoading(true);
        setError(null);

        try {
            let response;
            if (s3Key) {
                response = await complianceService.analyzeFromS3(s3Key, true);
            } else if (textInput.trim()) {
                response = await complianceService.analyzeDocumentHybrid(textInput);
            } else {
                setError('Please provide document text to analyze.');
                setLoading(false);
                return;
            }

            const analysisResult = response.data?.data || response.data;
            setResult(analysisResult);
            onAnalysisComplete?.(analysisResult);
        } catch (err: any) {
            console.error('[NLPAnalysisPanel] Analysis failed:', err);
            setError(err.response?.data?.message || 'Analysis failed');
        } finally {
            setLoading(false);
        }
    };

    const toggleAmendment = (id: string) => {
        setSelectedAmendments(prev =>
            prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]
        );
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-20 bg-white border rounded-3xl">
                <i className="fas fa-circle-notch fa-spin text-green-600 text-3xl mb-4"></i>
                <p className="font-bold text-gray-900 uppercase tracking-widest text-sm">Forensic Analysis in Progress...</p>
            </div>
        );
    }

    if (!result) {
        return (
            <div className="bg-white border rounded-3xl p-8 shadow-sm">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center text-green-600">
                        <i className="fas fa-brain"></i>
                    </div>
                    <div>
                        <h3 className="font-bold text-gray-900">Compliance Analysis</h3>
                        <p className="text-xs text-gray-500">Integrated UK Housing Law Validator</p>
                    </div>
                </div>

                {!s3Key && (
                    <div className="space-y-4">
                        <div className="flex gap-4">
                            <textarea
                                value={textInput}
                                onChange={(e) => setTextInput(e.target.value)}
                                placeholder="Paste agreement text here for forensic review..."
                                className="flex-1 h-40 p-4 border rounded-xl text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none transition-all"
                            />

                            <div className="w-48 flex flex-col gap-2">
                                <label className="flex flex-col items-center justify-center h-full border-2 border-dashed border-gray-200 rounded-xl hover:border-green-500 hover:bg-green-50 cursor-pointer transition-all group">
                                    <div className="flex flex-col items-center justify-center py-4">
                                        <i className="fas fa-file-upload text-gray-400 group-hover:text-green-500 text-xl mb-2"></i>
                                        <p className="text-[10px] font-bold text-gray-400 group-hover:text-green-600 uppercase tracking-tighter">Upload File</p>
                                    </div>
                                    <input
                                        type="file"
                                        className="hidden"
                                        accept=".pdf,.docx,.txt"
                                        onChange={async (e) => {
                                            const file = e.target.files?.[0];
                                            if (!file) return;

                                            const toBase64 = (f: File): Promise<string> => {
                                                return new Promise((resolve, reject) => {
                                                    const reader = new FileReader();
                                                    reader.readAsDataURL(f);
                                                    reader.onload = () => resolve((reader.result as string).split(',')[1]);
                                                    reader.onerror = error => reject(error);
                                                });
                                            };

                                            try {
                                                const { agreementService } = await import('../../../services/api');
                                                const base64Data = await toBase64(file);
                                                const res = await agreementService.extractText({
                                                    file: base64Data,
                                                    fileName: file.name,
                                                    mimeType: file.type
                                                });
                                                if (res.data?.success && res.data?.data?.text) {
                                                    setTextInput(res.data.data.text);
                                                }
                                            } catch (err: any) {
                                                setError('Failed to extract text from file.');
                                            } finally {
                                                setLoading(false);
                                            }
                                        }}
                                    />
                                </label>
                            </div>
                        </div>
                    </div>
                )}

                {error && (
                    <div className="p-4 bg-red-50 border border-red-100 text-red-600 rounded-xl text-xs mb-4 flex items-center gap-2">
                        <i className="fas fa-exclamation-circle"></i> {error}
                    </div>
                )}

                <button
                    onClick={runAnalysis}
                    className="w-full bg-green-600 text-white font-bold py-3 rounded-xl hover:bg-green-700 transition flex items-center justify-center gap-2 mt-4"
                >
                    <i className="fas fa-microscope"></i> RUN ANALYZE DOCUMENT
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6 bg-white border rounded-[2rem] p-8 shadow-sm animate-in fade-in duration-500">
            {/* Header with Score */}
            <div className="flex justify-between items-start">
                <div>
                    <h2 className="text-xl font-bold text-gray-900">
                        Tenancy Agreement Analysis Results
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">Review critical legal amendments below</p>
                </div>
                <div className="bg-[#f87171] text-white px-4 py-2 rounded-xl text-center shadow-lg">
                    <div className="text-2xl font-black">{result.score}%</div>
                    <div className="text-[10px] uppercase font-bold tracking-widest">Non Compliant</div>
                </div>
            </div>

            {/* Summary Box */}
            <div className="bg-white border border-gray-100 border-l-4 border-l-indigo-500 rounded-xl p-5">
                <p className="text-sm text-gray-600 leading-relaxed font-medium">
                    <span className="font-bold text-gray-800">Amendment Report:</span> {result.amendments?.length || 0} issue(s) found.
                    <span className="text-red-600 font-bold ml-1">🔴 CRITICAL ({result.amendments?.length || 0}):</span> These MUST be fixed to avoid legal liability.
                    {result.amendments?.map((a, i) => (
                        <span key={i} className="ml-1 text-gray-600">
                            {i + 1}. {a.issue.split('.')[0]}
                        </span>
                    ))}
                </p>
            </div>

            {/* Amendments List */}
            <div className="space-y-6">
                <div className="flex justify-between items-center">
                    <h3 className="font-bold text-gray-900">Amendments Required ({result.amendments?.length || 0})</h3>
                    <div className="flex gap-4 text-xs font-bold text-gray-500 uppercase tracking-tight">
                        <button className="hover:text-green-600 transition">Select All</button>
                        <button className="hover:text-red-500 transition">Clear</button>
                    </div>
                </div>

                {result.amendments?.map((amd, idx) => (
                    <div key={idx} className="bg-white border rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all">
                        <div className="p-6 flex gap-4">
                            <input
                                type="checkbox"
                                checked={selectedAmendments.includes(amd.id || idx.toString())}
                                onChange={() => toggleAmendment(amd.id || idx.toString())}
                                className="mt-1 w-5 h-5 accent-green-600 cursor-pointer"
                            />
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="bg-red-600 text-white text-[10px] font-black px-2 py-0.5 rounded uppercase tracking-tighter">CRITICAL</span>
                                    <span className="text-sm font-bold text-gray-800">{amd.section}</span>
                                    <span className="bg-indigo-50 text-indigo-600 text-[10px] font-bold px-2 py-0.5 rounded">
                                        {amd.legislationRef || 'RRA 2025'}
                                    </span>
                                </div>
                                <p className="text-sm text-gray-500 mb-4">{amd.issue}</p>

                                <div className="space-y-3">
                                    <div className="bg-red-50 border border-red-100 rounded-xl p-4">
                                        <h5 className="text-[10px] font-black text-red-600 uppercase mb-2">Before:</h5>
                                        <p className="text-sm text-gray-700 font-medium italic italic">"{amd.originalText}"</p>
                                    </div>
                                    <div className="bg-green-50 border border-green-100 rounded-xl p-4">
                                        <h5 className="text-[10px] font-black text-green-600 uppercase mb-2">After:</h5>
                                        <p className="text-sm text-gray-800 font-bold">{amd.amendedText}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Recommendations */}
            <div className="bg-[#fbfcff] border rounded-2xl p-6">
                <h4 className="font-bold text-gray-900 mb-4 uppercase tracking-wider text-xs">Recommendations</h4>
                <ul className="space-y-3">
                    {[
                        'Have amendments reviewed by a qualified solicitor before use',
                        'Ensure all pre-tenancy documents (Gas Safety, EPC, How to Rent) are served',
                        'Register deposit in a government-approved scheme within 30 days'
                    ].map((rec, i) => (
                        <li key={i} className="flex gap-3 text-sm text-gray-600 font-medium">
                            <i className="fas fa-check-circle text-green-500 mt-0.5"></i>
                            {rec}
                        </li>
                    ))}
                </ul>
            </div>

            <button
                onClick={() => alert(`Applied ${selectedAmendments.length} amendments.`)}
                className={`w-full py-4 rounded-xl text-white font-black text-lg shadow-xl transition-all ${selectedAmendments.length > 0 ? 'bg-indigo-400 hover:bg-indigo-500 active:scale-[0.98]' : 'bg-gray-200 cursor-not-allowed text-gray-400'}`}
            >
                Apply {selectedAmendments.length} Amendment(s)
            </button>
        </div>
    );
};
